import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import AuthLayout, { AuthNotice } from './AuthLayout';
import { authErrorKey, sendVerification, verificationWait, clearAuthCookie } from '../../utils/authFlow';

export default function EmailVerification() {
  const { language } = useLanguage();
  const { currentUser, refreshUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const completing = useRef(false);
  const [error, setError] = useState(location.state?.verificationError || '');
  const [notice, setNotice] = useState('');
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [wait, setWait] = useState(() => currentUser ? verificationWait(currentUser.uid) : 0);
  const initialized = useRef(false);
  const checkingRef = useRef(false);
  const sendingRef = useRef(false);
  const send = useCallback(async () => {
    if (!currentUser || sendingRef.current || verificationWait(currentUser.uid)) return;
    sendingRef.current = true;
    setSending(true); setError(''); setNotice('');
    try {
      await sendVerification(currentUser, language);
      setWait(60); setNotice('verify.sent');
    } catch (err) { setError(authErrorKey(err)); }
    finally { setSending(false); sendingRef.current = false; }
  }, [currentUser, language]);
  const check = useCallback(async (silent = false) => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    if (!silent) { setChecking(true); setError(''); setNotice(''); }
    try {
      const verified = await refreshUser();
      if (verified) {
        completing.current = true;
        const email = currentUser?.email;
        try {
          await signOut(auth);
          clearAuthCookie();
          navigate('/login', { replace: true, state: { from: location.state?.from, emailVerified: true, email } });
        } catch (err) { completing.current = false; throw err; }
      } else if (!silent) setError('verify.notYet');
      return verified;
    } catch (err) { if (!silent) setError(authErrorKey(err)); }
    finally { setChecking(false); checkingRef.current = false; }
  }, [refreshUser, currentUser?.email, navigate, location.state?.from]);
  useEffect(() => {
    if (initialized.current || !currentUser) return;
    initialized.current = true;
    check(true).then(verified => {
      if (verified === false && !verificationWait(currentUser.uid) && !location.state?.verificationError) send();
    });
  }, [check, currentUser, location.state?.verificationError, send]);
  useEffect(() => {
    const onReturn = () => { if (document.visibilityState === 'visible') check(true); };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [check]);
  useEffect(() => {
    if (!wait) return;
    const timer = setTimeout(() => setWait(value => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [wait]);
  if (!currentUser) return <Navigate to="/login" replace state={{ from: location.state?.from, emailVerified: completing.current }} />;
  return <EmailVerificationView email={currentUser.email} error={error} notice={notice} sending={sending}
    checking={checking} wait={wait} onCheck={() => check()} onSend={send} onExit={logout} />;
}

// Shared view lets the development preview exercise the real UI without auth effects.
export function EmailVerificationView({ email, error = '', notice = '', sending = false, checking = false,
  wait = 0, onCheck, onSend, onExit }) {
  const { t } = useTranslation('auth');
  return <AuthLayout centered mode="signup" title={t('verify.title')} subtitle={t('verify.subtitle')}
    footer={<button className="auth-text-button" type="button" onClick={onExit}>{t('verify.switchAccount')}</button>}>
    <bdi className="auth-verification-email" dir="ltr">{email}</bdi>
    <AuthNotice>{error && t(error)}</AuthNotice>
    <AuthNotice success>{notice && t(notice)}</AuthNotice>
    <button className="auth-primary" type="button" disabled={checking || sending} onClick={onCheck}>
      {t(checking ? 'verify.checking' : 'verify.continue')}
    </button>
    <div className="auth-verification-help">
      <p className="auth-hint">{t('verify.help')}</p>
      <button className="auth-text-button" type="button" disabled={sending || wait > 0} onClick={onSend}>
        {sending ? t('verify.sending') : wait ? t('verify.resendWait', { seconds: wait }) : t('verify.resend')}
      </button>
    </div>
    <p className="auth-hint">{t('verify.expiryHelp')}</p>
  </AuthLayout>;
}
